import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaSearch,
} from "react-icons/fa";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

const Staff = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [editStaff, setEditStaff] = useState(null);
  const [deleteStaffId, setDeleteStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role and Department options
  const roleOptions = [
    "Senior Technician",
    "Technician",
    "Manager",
    "Receptionist",
  ];
  const departmentOptions = [
    "Hardware",
    "Software",
    "Operations",
    "Frontdesk",
    "Logistics",
  ];

  const [privileges, setPrivileges] = useState(null);

  // Fetch privileges from localStorage
  useEffect(() => {
    const getaccess = localStorage.getItem("role");
    setPrivileges(getaccess);
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional for smooth scrolling
    });
  }, []);

  // Fetch all staff from API
  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Apiurl}/staff`);
      setStaff(response.data.reverse());
      setLoading(false);
    } catch (error) {
      console.error("Error fetching staff:", error);
      setError("Failed to fetch staff. Please try again.");
      setLoading(false);
    }
  };

  // Handle modal open/close
  const handleCloseAddModal = () => setShowAddModal(false);
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditStaff(null);
  };
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStaffId(null);
  };

  // Handle adding a staff member
  const handleAddStaff = async (e) => {
    e.preventDefault();
    const form = e.target;
    const staffData = {
      full_name: form.fullName.value,
      email: form.email.value,
      phone: form.phone.value,
      role: form.role.value,
      department: form.department.value,
      specialties: form.specialties.value,
      status: form.staffStatus.value,
    };

    try {
      const response = await axios.post(`${Apiurl}/staff`, staffData);
      setStaff([...staff, response.data]);
      form.reset();
      handleCloseAddModal();
    } catch (error) {
      console.error("Error adding staff:", error);
      setError("Failed to add staff. Please try again.");
    }
  };

  // Handle editing a staff member
  const handleEditStaff = async (e) => {
    e.preventDefault();
    const form = e.target;
    const staffData = {
      full_name: form.fullName.value,
      email: form.email.value,
      phone: form.phone.value,
      role: form.role.value,
      department: form.department.value,
      specialties: form.specialties.value,
      status: form.staffStatus.value,
    };

    try {
      const response = await axios.put(
        `${Apiurl}/staff/${editStaff._id}`,
        staffData,
      );
      setStaff(
        staff.map((member) =>
          member._id === editStaff._id ? response.data : member,
        ),
      );
      form.reset();
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating staff:", error);
      setError("Failed to update staff. Please try again.");
    }
  };

  // Handle delete staff
  const handleDeleteStaff = (id) => {
    setDeleteStaffId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${Apiurl}/staff/${deleteStaffId}`);
      setStaff(staff.filter((member) => member._id !== deleteStaffId));
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting staff:", error);
      setError("Failed to delete staff. Please try again.");
    }
  };

  // Handle pagination
  const filteredStaff = staff.filter(
    (member) =>
      member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.email &&
        member.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.phone &&
        member.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.role &&
        member.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.department &&
        member.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.specialties &&
        member.specialties.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const indexOfLastItem = currentPage * parseInt(itemsPerPage);
  const indexOfFirstItem = indexOfLastItem - parseInt(itemsPerPage);
  const currentStaff = filteredStaff.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStaff.length / parseInt(itemsPerPage));

  // Generate pagination with ellipsis logic
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div
        className="container-fluid p-3 d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {/* Error Alert */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-end mb-3">
        {(privileges === "Admin" ||
          privileges === "Manager" ||
          privileges === "Receptionist") && (
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#2e9290" }}
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus className="me-2" />
            Add Staff
          </button>
        )}
      </div>

      {/* Main Table Card */}
      <div className="card">
        <div className="card-body">
          {/* Controls */}
          <div className="row mb-2">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setItemsPerPage(e.target.value);
                  }}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FaSearch
                    className="text-muted"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    S.No
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Full Name
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Email
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Phone
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Role
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Department
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Specialties
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Status
                  </th>
                  <th style={{ backgroundColor: "#2e9290", color: "white" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentStaff.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      {searchTerm
                        ? "No staff found matching your search"
                        : "No staff available"}
                    </td>
                  </tr>
                ) : (
                  currentStaff.map((member, index) => (
                    <tr key={member._id} className="align-middle">
                      <td>
                        {(currentPage - 1) * parseInt(itemsPerPage) + index + 1}
                      </td>
                      <td>{member.full_name}</td>
                      <td>{member.email}</td>
                      <td>{member.phone}</td>
                      <td>{member.role}</td>
                      <td>{member.department}</td>
                      <td>{member.specialties}</td>
                      <td>
                        <span
                          className={`badge rounded-pill fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 ${
                            member.status === "Active"
                              ? "bg-success text-white"
                              : "bg-danger text-white"
                          }`}
                        >
                          <FaCheckCircle />
                          {member.status}
                        </span>
                      </td>
                      <td>
                        {(privileges === "Admin" ||
                          privileges === "Manager") && (
                          <button
                            className="btn btn-sm p-1 border-0"
                            style={{ backgroundColor: "transparent" }}
                            onClick={() => {
                              setEditStaff(member);
                              setShowEditModal(true);
                            }}
                          >
                            <FaEdit className="text-primary" />
                          </button>
                        )}

                        {privileges === "Admin" && (
                          <button
                            className="btn btn-sm p-1 border-0"
                            style={{ backgroundColor: "transparent" }}
                            onClick={() => handleDeleteStaff(member._id)}
                          >
                            <FaTrash className="text-danger" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline">Previous</span>
                </button>
                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#2e9290", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Add Staff</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddStaff}>
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      className="form-control"
                      placeholder="e.g., John Doe"
                      required
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="e.g., john@company.com"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        placeholder="Enter 10-digit number"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        inputMode="numeric"
                        required
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <select name="role" className="form-select" required>
                          <option value="">Select role</option>
                          {roleOptions.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Department</label>
                      <select
                        name="department"
                        className="form-select"
                        required
                      >
                        <option value="">Select department</option>
                        {departmentOptions.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Specialties (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="specialties"
                      className="form-control"
                      placeholder="e.g., Screen Repair, Battery Replacement"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="staffStatus"
                      className="form-select"
                      defaultValue="Active"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseAddModal}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ backgroundColor: "#2e9290", border: "none" }}
                    >
                      Add Staff
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Edit Staff</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditStaff}>
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      className="form-control"
                      defaultValue={editStaff ? editStaff.full_name : ""}
                      placeholder="e.g., John Doe"
                      required
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        defaultValue={editStaff ? editStaff.email : ""}
                        placeholder="e.g., john@company.com"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        defaultValue={editStaff ? editStaff.phone : ""}
                        placeholder="Enter 10-digit number"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        inputMode="numeric"
                        required
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Role</label>
                      <select
                        name="role"
                        className="form-select"
                        defaultValue={editStaff ? editStaff.role : ""}
                        required
                      >
                        <option value="">Select role</option>
                        {roleOptions.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Department</label>
                      <select
                        name="department"
                        className="form-select"
                        defaultValue={editStaff ? editStaff.department : ""}
                        required
                      >
                        <option value="">Select department</option>
                        {departmentOptions.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Specialties (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="specialties"
                      className="form-control"
                      defaultValue={editStaff ? editStaff.specialties : ""}
                      placeholder="e.g., Screen Repair, Battery Replacement"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="staffStatus"
                      className="form-select"
                      defaultValue={editStaff ? editStaff.status : "Active"}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseEditModal}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ backgroundColor: "#2e9290", border: "none" }}
                    >
                      Update Staff
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the staff{" "}
                  {staff.find((s) => s._id === deleteStaffId)?.full_name}?
                </p>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
